import { ChangeDetectionStrategy, Component, inject, input, OnInit, output, signal } from '@angular/core';
import { CreateRecruitGroupDto, RecruitGroupResponseDto } from '@/app/common/general-types/recruit-groups.dto';
import {
  embedRecruitGroupReview,
  hasRecruitGroupReview,
  parseRecruitGroupReview,
  visibleGroupDescription,
} from '@/app/common/general-types/recruit-groups.review';
import { FormField, form, minLength, required } from '@angular/forms/signals';
import { RecruitGroupService } from '@/app/services/recruit-group/recruit-group.service';
import { ToastNotificationService } from '@/app/atoms/toast-notification/toast-notification.service';
import { ViewportOverlayDirective } from '@/app/common/directives/viewport-overlay/viewport-overlay.directive';

@Component({
  selector: 'cmg-edit-group',
  imports: [FormField],
  hostDirectives: [ViewportOverlayDirective],
  templateUrl: './edit-group.html',
  styleUrl: './edit-group.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:keydown.escape)': 'closeEditGroup()'
  }
})
export class EditGroup implements OnInit {

  groupToEdit = input.required<RecruitGroupResponseDto>();

  close = output<RecruitGroupResponseDto | undefined>();

  groupUpdated = signal<boolean>(false);
  submitInProgress = signal<boolean>(false);
  private lastUpdatedGroup = signal<RecruitGroupResponseDto | undefined>(undefined);

  editGroupModel = signal<{ groupName: string; groupDescription: string }>({
    groupName: '',
    groupDescription: '',
  });

  editGroupForm = form(this.editGroupModel, (schemaPath) => {
    required(schemaPath.groupName, { message: 'Group name is required' });
    minLength(schemaPath.groupName, 2, { message: 'Group name must be at least 2 characters long' });
  });

  private recruitGroupService = inject(RecruitGroupService);
  private toastNotificationService = inject(ToastNotificationService);

  ngOnInit(): void {
    this.editGroupModel.set({
      groupName: this.groupToEdit().groupName,
      groupDescription: visibleGroupDescription(this.groupToEdit().groupDescription),
    });
  }

  saveGroup(): void {
    this.submitInProgress.set(true);

    const group = this.groupToEdit();
    const review = parseRecruitGroupReview(group);
    const nextDescription = this.editGroupModel().groupDescription.trim();
    const groupDescription = hasRecruitGroupReview(review)
      ? embedRecruitGroupReview(nextDescription, review)
      : nextDescription;
    const updateDto: CreateRecruitGroupDto = {
      groupName: this.editGroupModel().groupName,
      groupDescription,
      createdBy: group.createdBy,
      isGlobal: group.isGlobal,
      businessAccountId: group.businessAccountId,
      agencyAccountId: group.agencyAccountId,
      group_type: group.group_type,
      syncWithNoodle: group.syncWithNoodle,
      sentToNoodle: group.sentToNoodle,
      briefId: group.briefId,
      briefType: group.briefType,
      allowReviewerInvite: group.allowReviewerInvite,
      reviewerInviteBriefId: group.reviewerInviteBriefId,
    };

    this.recruitGroupService.updateRecruitGroup(this.groupToEdit().id, updateDto).subscribe({
      next: (response) => {
        this.submitInProgress.set(false);
        this.lastUpdatedGroup.set(response);
        this.groupUpdated.set(true);
      },
      error: () => {
        this.submitInProgress.set(false);
        this.toastNotificationService.alert({
          title: 'Failed to update group',
          message: 'Could not update the group. Please try again.',
          type: 'error',
          icon: 'error',
        });
      },
    });
  }

  closeEditGroup(): void {
    const updated = this.lastUpdatedGroup();
    this.lastUpdatedGroup.set(undefined);
    this.groupUpdated.set(false);
    this.close.emit(updated);
  }
}
